import { CurrencyCode } from "./currencies";
import { formatSimpleMoney } from "./index";

type BudgetFilterOption = {
  label: string;
  filterValue: [number, number];
};

export type CurrencyBudgetConfig = {
  maxBudgetValue: number;
  budgetSteps: number;
  amountOptions: string[];
  budgetFilterOptions: BudgetFilterOption[];
};

const formatFilterLabel = (
  amount: number,
  currency: string,
  suffix = false,
): string => {
  if (amount === 0) return formatSimpleMoney({ amount: "0", currency });
  return (
    formatSimpleMoney({ amount: String(amount), currency }) +
    (suffix ? "+" : "")
  );
};

const buildBudgetFilters = (
  ranges: { min: number; max: number }[],
  currency: string,
): BudgetFilterOption[] => {
  const options: BudgetFilterOption[] = [
    { label: formatFilterLabel(0, currency), filterValue: [0, 0] },
  ];
  ranges.forEach(({ min, max }, index) => {
    const isLast = index === ranges.length - 1;
    if (isLast) {
      options.push({
        label: formatFilterLabel(min, currency, true),
        filterValue: [min, 999999999],
      });
    } else {
      options.push({
        label: `${formatFilterLabel(min, currency)} a ${formatFilterLabel(max, currency)}`,
        filterValue: [min, max],
      });
    }
  });
  return options;
};

const clpBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 200000,
  budgetSteps: 40,
  amountOptions: ["0", "20000", "30000", "40000", "50000"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 10000 },
      { min: 10000, max: 30000 },
      { min: 30000, max: 50000 },
      { min: 50000, max: 100000 },
      { min: 100000, max: 999999999 },
    ],
    "CLP",
  ),
};

const penBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 800,
  budgetSteps: 40,
  amountOptions: ["0", "80", "120", "160", "200"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 40 },
      { min: 40, max: 120 },
      { min: 120, max: 200 },
      { min: 200, max: 400 },
      { min: 400, max: 999999999 },
    ],
    "PEN",
  ),
};

// Derived from the CLP config by approximate exchange rate, pending
// product-defined ranges.
const arsBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 300000,
  budgetSteps: 40,
  amountOptions: ["0", "30000", "45000", "60000", "75000"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 15000 },
      { min: 15000, max: 45000 },
      { min: 45000, max: 75000 },
      { min: 75000, max: 150000 },
      { min: 150000, max: 999999999 },
    ],
    "ARS",
  ),
};

// Derived from the CLP config by approximate exchange rate, pending
// product-defined ranges.
const copBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 800000,
  budgetSteps: 40,
  amountOptions: ["0", "80000", "120000", "160000", "200000"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 40000 },
      { min: 40000, max: 120000 },
      { min: 120000, max: 200000 },
      { min: 200000, max: 400000 },
      { min: 400000, max: 999999999 },
    ],
    "COP",
  ),
};

// Derived from the CLP config by approximate exchange rate, pending
// product-defined ranges.
const usdBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 200,
  budgetSteps: 40,
  amountOptions: ["0", "20", "30", "40", "50"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 10 },
      { min: 10, max: 30 },
      { min: 30, max: 50 },
      { min: 50, max: 100 },
      { min: 100, max: 999999999 },
    ],
    "USD",
  ),
};

// Derived from the CLP config by approximate exchange rate, pending
// product-defined ranges.
const brlBudgetConfig: CurrencyBudgetConfig = {
  maxBudgetValue: 1000,
  budgetSteps: 40,
  amountOptions: ["0", "100", "150", "200", "250"],
  budgetFilterOptions: buildBudgetFilters(
    [
      { min: 0, max: 50 },
      { min: 50, max: 150 },
      { min: 150, max: 250 },
      { min: 250, max: 500 },
      { min: 500, max: 999999999 },
    ],
    "BRL",
  ),
};

const currencyBudgetConfigs: Record<CurrencyCode, CurrencyBudgetConfig> = {
  CLP: clpBudgetConfig,
  PEN: penBudgetConfig,
  ARS: arsBudgetConfig,
  COP: copBudgetConfig,
  USD: usdBudgetConfig,
  BRL: brlBudgetConfig,
};

export const getCurrencyBudgetConfig = (
  currency: CurrencyCode,
): CurrencyBudgetConfig => currencyBudgetConfigs[currency];
